import { css } from '@emotion/react';

export const prose = css`
& {
  line-height: 1.45;

  /* Paragraphs */
  p {
    margin: 0;
    font-size: inherit;
  }

  * + p {
    margin-top: 12px;
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: 500;
    line-height: 1.3;
  }

  * + h1 { margin-top: 28px; }
  * + h2 { margin-top: 24px; }
  * + h3 { margin-top: 20px; }
  * + h4, * + h5, * + h6 { margin-top: 16px; }

  /* Tight spacing after headings - explicit selectors for specificity */
  h1 + p, h2 + p, h3 + p, h4 + p, h5 + p, h6 + p { margin-top: 4px; }
  h1 + ul, h2 + ul, h3 + ul, h4 + ul, h5 + ul, h6 + ul { margin-top: 6px; }
  h1 + ol, h2 + ol, h3 + ol, h4 + ol, h5 + ol, h6 + ol { margin-top: 6px; }
  h1 + pre, h2 + pre, h3 + pre, h4 + pre, h5 + pre, h6 + pre { margin-top: 8px; }
  h1 + blockquote, h2 + blockquote, h3 + blockquote, h4 + blockquote, h5 + blockquote, h6 + blockquote { margin-top: 8px; }
  h1 + table, h2 + table, h3 + table, h4 + table, h5 + table, h6 + table { margin-top: 8px; }

  h1 {
    font-size: 1.35em;
  }

  h2 {
    font-size: 1.15em;
  }

  h3 {
    font-size: 1.05em;
  }

  h4, h5, h6 {
    font-size: 1em;
  }

  /* Horizontal rules */
  hr {
    margin: 24px 0;
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  hr + * {
    margin-top: 0;
  }

  /* Bold text */
  strong, b {
    font-weight: 600;
  }

  /* Inline code */
  code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    background-color: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 4px;
  }

  /* Code blocks */
  pre {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    background-color: rgba(0, 0, 0, 0.04);
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0;

    code {
      background: none;
      padding: 0;
      border-radius: 0;
    }
  }

  * + pre {
    margin-top: 12px;
  }

  /* Lists */
  ul, ol {
    margin: 0;
    padding-left: 24px;
  }

  * + ul, * + ol {
    margin-top: 10px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin: 0;
    padding-left: 4px;
  }

  li + li {
    margin-top: 4px;
  }

  li > ul, li > ol {
    margin-top: 4px;
  }

  /* Links */
  a {
    color: #1976d2;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Blockquotes */
  blockquote {
    margin: 0;
    padding: 12px 16px;
    border-left: 4px solid rgba(0, 0, 0, 0.12);
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 0 8px 8px 0;

    p {
      margin: 0;
    }

    p + p {
      margin-top: 8px;
    }
  }

  * + blockquote {
    margin-top: 12px;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }

  * + table {
    margin-top: 12px;
  }

  th, td {
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    text-align: left;
  }

  th {
    background-color: rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 0;
  }

  * + img {
    margin-top: 16px;
  }

  /* Emphasis */
  em, i {
    font-style: italic;
  }

  /* Strikethrough */
  del, s {
    text-decoration: line-through;
  }

  /* Task list checkboxes (GFM) */
  input[type="checkbox"] {
    margin-right: 8px;
    accent-color: #1976d2;
    vertical-align: middle;
  }

  li:has(> input[type="checkbox"]) {
    list-style: none;
    margin-left: -24px;
  }

  /* Keyboard shortcuts */
  kbd {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    padding: 2px 6px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.04);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.12);
  }
}`;

export const proseCompact = css`
& {
  line-height: 1.4;

  /* Paragraphs */
  p {
    margin: 0;
    font-size: inherit;
  }

  * + p {
    margin-top: 8px;
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: 500;
    line-height: 1.3;
  }

  * + h1 { margin-top: 20px; }
  * + h2 { margin-top: 16px; }
  * + h3 { margin-top: 14px; }
  * + h4, * + h5, * + h6 { margin-top: 12px; }

  /* Tight spacing after headings - explicit selectors for specificity */
  h1 + p, h2 + p, h3 + p, h4 + p, h5 + p, h6 + p { margin-top: 3px; }
  h1 + ul, h2 + ul, h3 + ul, h4 + ul, h5 + ul, h6 + ul { margin-top: 4px; }
  h1 + ol, h2 + ol, h3 + ol, h4 + ol, h5 + ol, h6 + ol { margin-top: 4px; }
  h1 + pre, h2 + pre, h3 + pre, h4 + pre, h5 + pre, h6 + pre { margin-top: 6px; }
  h1 + blockquote, h2 + blockquote, h3 + blockquote, h4 + blockquote, h5 + blockquote, h6 + blockquote { margin-top: 6px; }
  h1 + table, h2 + table, h3 + table, h4 + table, h5 + table, h6 + table { margin-top: 6px; }

  h1 {
    font-size: 1.25em;
  }

  h2 {
    font-size: 1.1em;
  }

  h3 {
    font-size: 1em;
  }

  h4, h5, h6 {
    font-size: 0.95em;
  }

  /* Horizontal rules */
  hr {
    margin: 12px 0;
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  hr + * {
    margin-top: 0;
  }

  /* Bold text */
  strong, b {
    font-weight: 600;
  }

  /* Inline code */
  code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    background-color: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 4px;
  }

  /* Code blocks */
  pre {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    background-color: rgba(0, 0, 0, 0.04);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0;

    code {
      background: none;
      padding: 0;
      border-radius: 0;
    }
  }

  * + pre {
    margin-top: 8px;
  }

  /* Lists */
  ul, ol {
    margin: 0;
    padding-left: 20px;
  }

  * + ul, * + ol {
    margin-top: 6px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin: 0;
    padding-left: 4px;
  }

  li + li {
    margin-top: 2px;
  }

  li > ul, li > ol {
    margin-top: 2px;
  }

  /* Links */
  a {
    color: #1976d2;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Blockquotes */
  blockquote {
    margin: 0;
    padding: 8px 12px;
    border-left: 3px solid rgba(0, 0, 0, 0.12);
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 0 6px 6px 0;

    p {
      margin: 0;
    }

    p + p {
      margin-top: 4px;
    }
  }

  * + blockquote {
    margin-top: 8px;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }

  * + table {
    margin-top: 8px;
  }

  th, td {
    padding: 6px 10px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    text-align: left;
  }

  th {
    background-color: rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 0;
  }

  * + img {
    margin-top: 10px;
  }

  /* Emphasis */
  em, i {
    font-style: italic;
  }

  /* Strikethrough */
  del, s {
    text-decoration: line-through;
  }

  /* Task list checkboxes (GFM) */
  input[type="checkbox"] {
    margin-right: 6px;
    accent-color: #1976d2;
    vertical-align: middle;
  }

  li:has(> input[type="checkbox"]) {
    list-style: none;
    margin-left: -20px;
  }

  /* Keyboard shortcuts */
  kbd {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.875em;
    padding: 2px 5px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.04);
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.12);
  }
}`;
